import { useEffect, useRef, useState } from 'react';

export default function FeaturesSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: '🤗',
      title: '「わからない」を叱らない',
      description: '非エンジニア目線で、つまずきポイントを完全網羅。お子様のペースに合わせて、優しく丁寧に指導します。',
      color: 'from-pink-400 to-pink-600',
      bgColor: 'bg-pink-50',
      delay: '0s'
    },
    {
      icon: '🏫',
      title: '梅田＋オンラインのハイブリッド',
      description: '最初は梅田で対面、慣れたら自宅で。お子様の成長に合わせて、最適な学習スタイルを選べます。',
      color: 'from-purple-400 to-purple-600',
      bgColor: 'bg-purple-50',
      delay: '0.2s'
    },
    {
      icon: '💰',
      title: '月謝1万円の明朗会計',
      description: '追加費用なし。PCレンタル対応で初期費用を抑える。安心して始められる料金設定です。',
      color: 'from-yellow-400 to-yellow-600',
      bgColor: 'bg-yellow-50',
      delay: '0.4s'
    }
  ];

  return (
    <section ref={sectionRef} id="features" className="py-12 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-pink-50 to-purple-50 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-20 right-10 w-64 h-64 bg-yellow-200 rounded-full opacity-10 blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 left-10 w-64 h-64 bg-pink-200 rounded-full opacity-10 blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>

      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className={`text-center mb-10 sm:mb-16 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="inline-block bg-purple-100 px-4 sm:px-6 py-2 rounded-full mb-4 sm:mb-6">
            <span className="text-purple-600 font-bold text-xs sm:text-sm">3つの特徴</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-800 mb-4 sm:mb-6 px-2">
            <span className="bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
              他のスクールとは、ここが違う！
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-2">
            非エンジニアのパパだからこそ分かる、本当に必要なサポート
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-20">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`${feature.bgColor} rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-xl hover:shadow-2xl transform hover:-translate-y-4 transition-all duration-500 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
              style={{ transitionDelay: feature.delay }}
            >
              <div className="text-center mb-4 sm:mb-6">
                <div className="inline-flex items-center justify-center w-20 sm:w-24 h-20 sm:h-24 bg-white rounded-full shadow-lg mb-4 sm:mb-6 animate-bounce" style={{ animationDuration: '2s', animationDelay: feature.delay }}>
                  <span className="text-5xl sm:text-6xl">{feature.icon}</span>
                </div>
                <h3 className={`text-xl sm:text-2xl font-black mb-3 sm:mb-4 bg-gradient-to-r ${feature.color} bg-clip-text text-transparent`}>
                  {feature.title}
                </h3>
              </div>
              <p className="text-gray-700 text-base sm:text-lg leading-relaxed text-center">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Comparison Table */}
        <div className={`bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 shadow-2xl transform transition-all duration-1000 delay-500 ${isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>
          <div className="text-center mb-6 sm:mb-10">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-800 mb-3 sm:mb-4 px-2">
              <span className="bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
                選ばれる理由
              </span>
            </h3>
            <p className="text-base sm:text-lg text-gray-600">大手スクールとの比較</p>
          </div>

          <div className="overflow-x-auto -mx-6 sm:mx-0 px-6 sm:px-0">
            <table className="w-full min-w-[500px]">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="py-3 sm:py-4 px-3 sm:px-6 text-left text-sm sm:text-lg font-bold text-gray-700"></th>
                  <th className="py-3 sm:py-4 px-3 sm:px-6 text-center">
                    <div className="bg-gradient-to-r from-pink-400 to-purple-400 text-white px-3 sm:px-6 py-2 sm:py-3 rounded-full font-black text-sm sm:text-lg inline-block whitespace-nowrap">
                      当スクール
                    </div>
                  </th>
                  <th className="py-3 sm:py-4 px-3 sm:px-6 text-center text-sm sm:text-lg font-bold text-gray-500 whitespace-nowrap">大手スクール</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100 hover:bg-pink-50 transition-colors duration-300">
                  <td className="py-4 sm:py-6 px-3 sm:px-6 font-bold text-gray-700 text-sm sm:text-base">目的</td>
                  <td className="py-4 sm:py-6 px-3 sm:px-6 text-center">
                    <span className="text-pink-600 font-bold text-sm sm:text-lg">PCを武器にする<br />（難易度やさしい）</span>
                  </td>
                  <td className="py-4 sm:py-6 px-3 sm:px-6 text-center text-gray-500 text-sm sm:text-base">エンジニア養成<br />（難易度高）</td>
                </tr>
                <tr className="border-b border-gray-100 hover:bg-pink-50 transition-colors duration-300">
                  <td className="py-4 sm:py-6 px-3 sm:px-6 font-bold text-gray-700 text-sm sm:text-base">月謝</td>
                  <td className="py-4 sm:py-6 px-3 sm:px-6 text-center">
                    <span className="text-pink-600 font-black text-xl sm:text-2xl">1万円</span>
                  </td>
                  <td className="py-4 sm:py-6 px-3 sm:px-6 text-center text-gray-500 text-sm sm:text-base whitespace-nowrap">1.5〜2万円</td>
                </tr>
                <tr className="hover:bg-pink-50 transition-colors duration-300">
                  <td className="py-4 sm:py-6 px-3 sm:px-6 font-bold text-gray-700 text-sm sm:text-base">指導スタイル</td>
                  <td className="py-4 sm:py-6 px-3 sm:px-6 text-center">
                    <span className="text-pink-600 font-bold text-sm sm:text-lg">親心に寄り添う指導</span>
                  </td>
                  <td className="py-4 sm:py-6 px-3 sm:px-6 text-center text-gray-500 text-sm sm:text-base">機械的な指導</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 sm:mt-10 text-center">
            <div className="inline-flex items-center space-x-2 sm:space-x-3 bg-gradient-to-r from-pink-100 to-purple-100 px-4 sm:px-8 py-3 sm:py-4 rounded-full">
              <span className="text-2xl sm:text-3xl">✨</span>
              <span className="text-sm sm:text-lg font-bold text-gray-800">お子様の「できた！」を一番大切にします</span>
              <span className="text-2xl sm:text-3xl">✨</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
